import React from 'react';
import { STATUS_TONES, PILL_SHELL } from './pillStyles';
import {
  getStatusVariant,
  formatStatusForDisplay,
  type StatusVocabulary,
  type StatusVariant,
} from '../../utils/statusUtils';

// StatusBadge — the one way to render a task status, a Work ID stage, a PCR stage or a project
// history stage.
//
// Why this exists. Twelve components carried their own status→colour and status→label mappings
// (`getStatusBadgeClass`, `statusBadgeVariant`, `statusVariant`, `getStatusBadge`,
// `getStatusBadgeVariant`, one per feature). They had drifted, which is what QA saw: the same
// task showed a different badge on the task table, the history panel and the phone layout, and
// "Active - Marked as Complete" rendered as a green tick on one surface and a blue badge on
// another. Colour comes from `getStatusVariant` and wording from `formatStatusForDisplay`, both
// in `utils/statusUtils` — change either there and every surface follows.
//
// The badge is `shrink-0` and never wraps: in a table row it sits beside an expand chevron, and
// letting it flex was what pushed it into the chevron on the quantified task list.

export interface StatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Raw status/stage value from the API (case-insensitive, e.g. `ACTIVE`, `internal_review`). */
  status: string | null | undefined;
  /** Override the derived wording. Use only where the surface genuinely says something else. */
  label?: React.ReactNode;
  /** Rounded-full instead of rounded. */
  pill?: boolean;
  /** A leading Bootstrap icon class, e.g. `bi-check-lg`. */
  icon?: string;
  /** What to render when there is no status at all. Defaults to nothing. */
  fallback?: React.ReactNode;
  /**
   * Which status vocabulary `status` belongs to. Work IDs read CLOSED as "request finished"
   * (muted) where a task reads it as a terminal cost state (dark), so Work ID surfaces should
   * pass `"workId"`. Everything else can leave it alone.
   */
  vocabulary?: StatusVocabulary;
  /**
   * Tinted-with-border treatment instead of a solid fill — a quieter badge for a page-level
   * status that should read as a label rather than compete with the page's actions. Used by the
   * project detail header, which has always looked this way.
   */
  soft?: boolean;
  /**
   * Escape hatch for values that are not domain statuses at all and so cannot be keyed by any
   * vocabulary — an HTTP response code from the mail provider, for instance, where the caller
   * derives the colour from a numeric range. Prefer adding a vocabulary in `statusUtils` over
   * reaching for this; it exists so that such a caller still gets the shared badge shell instead
   * of hand-rolling one (or overriding these classes with `!important`).
   */
  variant?: StatusVariant;
}

export { STATUS_TONES, PILL_SHELL };

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  // `pill` and `soft` are accepted for compatibility; every status badge is now the one pill.
  pill: _pill,
  icon,
  fallback = null,
  vocabulary = 'task',
  soft: _soft,
  variant: variantOverride,
  className = '',
  ...rest
}) => {
  if (!status) return <>{fallback}</>;

  const variant = variantOverride ?? getStatusVariant(status, vocabulary);
  const text = label ?? formatStatusForDisplay(status);

  // The badge never wraps, so in a narrow table column its cell has to clip it (max-w-full keeps
  // it inside the cell; the inner span ellipsizes). A clip alone would leave the label cut
  // mid-word with no way to read it, so carry the full text as a title. An explicit `title`
  // from the caller still wins.
  const title = rest.title ?? (typeof text === 'string' ? text : undefined);

  return (
    <span
      data-slot="badge"
      data-tone={variant}
      className={`${PILL_SHELL} ${STATUS_TONES[variant] ?? STATUS_TONES.secondary} ${className}`}
      {...rest}
      title={title}
    >
      {icon && <i className={`bi ${icon} tw-shrink-0`} aria-hidden="true" />}
      <span className="tw-min-w-0 tw-overflow-hidden tw-text-ellipsis">{text}</span>
    </span>
  );
};

export default StatusBadge;
